import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Tooltip } from 'antd';
import type { MouseEvent } from 'react';

import { colorConstants } from 'config/colorConstants';
import {
    PPA_PACK_ARCH_TABLE_SEARCH,
    PPA_PACK_ARCH_TABLE_SEARCH_SUBMIT,
    ppaPackArchTableKpiTestId,
} from 'constants/ppaTestIds';

import { PackArchitectureTableKpiCheckbox } from './PackArchitectureTableKpiCheckbox';
import {
    TableSearchButton,
    TableSearchGroup,
    TableToolbar,
} from './PackArchitectureTable.styles';
import type { KpiOption } from './packArchitectureTable.helpers';

type PackArchitectureTableToolbarProps = {
    tableTitle: string;
    tooltip: string | null;
    kpiOptions: KpiOption[];
    selectedKpis: string[];
    search: string;
    setSearch: (value: string) => void;
    onSearchSubmit: (override?: string) => void;
    onToggleKpi: (kpiId: string) => void;
    onMouseDown: (event: MouseEvent<HTMLElement>) => void;
};

const PackArchitectureTableToolbar = ({
    tableTitle,
    tooltip,
    kpiOptions,
    selectedKpis,
    search,
    setSearch,
    onSearchSubmit,
    onToggleKpi,
    onMouseDown,
}: PackArchitectureTableToolbarProps) => (
    <TableToolbar onMouseDown={onMouseDown}>
        <div className="toolbar-left">
            <div className="toolbar-title">
                <span className="toolbar-title-text">{tableTitle}</span>
                <Tooltip
                    placement="right"
                    color={colorConstants.blackAlt1}
                    title={tooltip || undefined}
                >
                    <InfoCircleOutlined
                        className="toolbar-title-icon"
                        aria-label={tooltip ? 'Table information' : undefined}
                    />
                </Tooltip>
            </div>

            <div className="toolbar-checkboxes">
                {kpiOptions.map((option) => (
                    <PackArchitectureTableKpiCheckbox
                        key={option.id}
                        option={option}
                        isChecked={selectedKpis.includes(option.id)}
                        selectedKpiCount={selectedKpis.length}
                        onToggle={onToggleKpi}
                        data-testid={ppaPackArchTableKpiTestId(option.id)}
                    />
                ))}
            </div>
        </div>

        <div className="toolbar-search">
            <TableSearchGroup>
                <Input
                    className="table-search-input"
                    bordered={false}
                    placeholder="Search"
                    value={search}
                    data-testid={PPA_PACK_ARCH_TABLE_SEARCH}
                    onChange={(event) => {
                        const value = event.target.value;
                        setSearch(value);
                        if (value === '') {
                            onSearchSubmit('');
                        }
                    }}
                    onPressEnter={() => onSearchSubmit()}
                    allowClear
                    aria-label="Search table rows"
                />
                <TableSearchButton
                    type="button"
                    onClick={() => onSearchSubmit()}
                    aria-label="Submit table search"
                    data-testid={PPA_PACK_ARCH_TABLE_SEARCH_SUBMIT}
                >
                    <SearchOutlined />
                </TableSearchButton>
            </TableSearchGroup>
        </div>
    </TableToolbar>
);

export default PackArchitectureTableToolbar;
